<template>
  <nav class="app-navbar" :class="{ 'app-navbar--admin': isAdminPage }">
    <div class="app-navbar__wrapper">
      <a href="/locations" class="app-navbar__logo">
        <img
          v-if="globalStore.wlc.logo"
          :src="globalStore.wlc.logo"
          :alt="globalStore.wlc.brand">
      </a>

      <button
        class="app-navbar__toggle"
        type="button"
        @click="mobileMenuOpen = !mobileMenuOpen">
        <span class="app-navbar__current">{{ currentPageLabel }}</span>
        <i class="fa-solid fa-chevron-down text-xs" />
      </button>

      <div
        v-if="mobileMenuOpen"
        class="app-navbar__backdrop"
        @click="mobileMenuOpen = false" />

      <div class="app-navbar__list" :class="{ 'app-navbar__list--open': mobileMenuOpen }">
        <button
          type="button"
          class="app-navbar__close"
          @click="mobileMenuOpen = false">
          <i class="fa-solid fa-xmark" />
        </button>
        <ul>
          <li v-for="({ href, current, label, items }) in mainNavItems" :key="label">
            <AppLink
              v-if="!items"
              :href="href"
              class="app-navbar__item"
              :class="{ current }">
              {{ label }}
            </AppLink>
            <NavbarDropdown
              v-else
              :items="items"
              :current="current">
              {{ label }}
            </NavbarDropdown>
          </li>
        </ul>
      </div>

      <button
        v-if="assistantStore.available"
        v-tooltip="'AI Assistant'"
        type="button"
        class="app-navbar__assistant"
        aria-label="AI Assistant"
        @click="assistantStore.open()">
        <i class="fa-solid fa-sparkles" />
      </button>

      <button
        v-tooltip="'Changelog'"
        type="button"
        class="app-navbar__changelog"
        aria-label="Changelog"
        @click="changelogStore.open()">
        <i class="fa-solid fa-bullhorn" />
        <span v-if="changelogStore.unread" class="app-navbar__changelog-dot" />
      </button>

      <UserMenu />
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useGlobalStore } from '@/stores/global'
import { useCurrentUrl } from '@/composables/useCurrentUrl'
import { useChangelogStore } from '@/stores/changelog'
import { useAssistantStore } from '@/stores/assistant'
import adminNavConfig from '@/config/navigationAdmin'
import navConfig from '@/config/navigation'

import UserMenu from './UserMenu.vue'
import NavbarDropdown from './NavbarDropdown.vue'

const globalStore = useGlobalStore()
const changelogStore = useChangelogStore()
const assistantStore = useAssistantStore()
const mobileMenuOpen = ref(false)
const currentPageUrl = useCurrentUrl()

// Client-side navigations don't reload the page, so close the menu ourselves
watch(currentPageUrl, () => {
  mobileMenuOpen.value = false
})

// Static on purpose: admin and user spaces never share one router session.
// Must become route-reactive before any admin feature adopts the router.
onMounted(() => changelogStore.fetchUnread())

const isAdminPage = window.location.pathname.startsWith('/admin')
const activeNavConfig = isAdminPage ? adminNavConfig : navConfig

function isCurrent(item) {
  // An item that opts into URL matching never falls back to the controller
  // path: globalStore.currentPath freezes on the first full load, so under
  // client-side navigation a stale prefix would keep the item lit after the
  // user left the feature.
  if (item.urlPrefix || item.activeUrls) {
    const currentUrl = currentPageUrl.value
    if (item.urlPrefix && currentUrl.startsWith(item.urlPrefix)) return true

    return Boolean(item.activeUrls?.some((u) => currentUrl.startsWith(u)))
  }

  // Legacy controller-path matching (per-page mounts only)
  const path = globalStore.currentPath
  if (!path) return false

  if (item.pathPrefix && path.startsWith(item.pathPrefix)) {
    return true
  }

  if (item.activePaths) {
    return item.activePaths.some((p) => path.startsWith(p))
  }

  return false
}

function getLabel(item) {
  if (item.dynamicLabel) {
    return item.dynamicLabel(globalStore)
  }
  return item.label
}

function enrichItem(item) {
  return { ...item, label: getLabel(item), current: isCurrent(item) }
}

function isVisible(item) {
  return !item.show || item.show(globalStore)
}

const mainNavItems = computed(() => activeNavConfig
  .filter(isVisible)
  .map((item) => {
    if (!item.items) return enrichItem(item)

    const visibleSubItems = item.items.filter(isVisible).map(enrichItem)
    return {
      ...item,
      label: getLabel(item),
      items: visibleSubItems,
      current: visibleSubItems.some((sub) => sub.current)
    }
  })
  .filter((item) => !item.items || item.items.length > 0))

const currentPageLabel = computed(() => {
  const current = mainNavItems.value.find((item) => item.current)
  if (!current) return 'Menu'
  if (current.items) {
    return current.items.find((sub) => sub.current)?.label || current.label
  }
  return current.label
})
</script>
